import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Menu, 
  MenuItem, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import axios from 'axios';

function MedicamentSuggestions() {
  const [medicaments, setMedicaments] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMedicamentId, setSelectedMedicamentId] = useState(null);
  const [selectedMedicament, setSelectedMedicament] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const handleClick = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setSelectedMedicamentId(userId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedMedicamentId(null);
  };

  const handleDelete = async () => {
    setMedicaments(medicaments => medicaments.filter(medicament => medicament._id !== selectedMedicamentId));
    await axios.post("delete_medicine", { medicineId: selectedMedicamentId}, {
      headers: { 
        "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
        Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
      },
    });
    handleClose();
  };

  const handleRowClick = (medicament) => {
    setSelectedMedicament(medicament);
    setDetailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedMedicament(null);
  };

  const renderField = (label, value) => {
    if (value === null || value === undefined || value === '') {
      return `${label}: Not specified`;
    }
    if (Array.isArray(value) && value.length === 0) {
      return `${label}: Not specified`;
    }
    if (typeof value === 'object' && Object.keys(value).length === 0) {
      return `${label}: Not specified`;
    }
    return `${label}: ${JSON.stringify(value, null, 2)}`;
  };

  const renderCompatibilities = (compatibilites) => {
    if (!compatibilites) return "Compatibilities: Not specified";
    
    return (
      <Box>
        <Typography variant="subtitle1">Compatibilities:</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={`Femme Enceinte: ${compatibilites.femmeEnceinte || 'Not specified'}`} />
          </ListItem>
          <ListItem>
            <ListItemText primary={`Allaitement: ${compatibilites.allaitement || 'Not specified'}`} />
          </ListItem>
          <ListItem>
            <ListItemText primary={`HTA: ${compatibilites.HTA || 'Not specified'}`} />
          </ListItem>
          <ListItem>
            <ListItemText primary={`Diabete: ${compatibilites.diabete || 'Not specified'}`} />
          </ListItem>
          <ListItem>
            <ListItemText primary={`Epilepsie: ${compatibilites.epilepsie || 'Not specified'}`} />
          </ListItem>
        </List>
      </Box>
    );
  };

  const renderPosologies = (posologies) => {
    if (!posologies || posologies.length === 0) return "Posologies: Not specified";
    
    return (
      <Box>
        <Typography variant="subtitle1">Posologies:</Typography>
        {posologies.map((poso, index) => (
          <Paper key={index} elevation={2} sx={{ p: 2, mb: 2 }}>
            <List dense>
              <ListItem>
                <ListItemText primary={`Dose: ${poso.dose || 'Not specified'}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Frequence: ${poso.frequence || 'Not specified'}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Age spécifique: ${poso.age_specifique_medic || 'Not specified'}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Age minimum: ${poso.min_age_enfant || 'Not specified'} ${poso.ageMinUnit || ''}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Age maximum: ${poso.max_age_enfant || 'Not specified'} ${poso.ageMaxUnit || ''}`} />
              </ListItem>
            </List>
          </Paper>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    const fetchMedicamentSuggestions = async () => {
      try {
        const response = await axios.get("get_medicament_suggestions", {
          headers: { 
            "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
            Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
          },
        });
        setMedicaments([...response.data].reverse());
      } catch (error) {
        console.log(error);
      }
    }

    fetchMedicamentSuggestions();
  }, []);

  return (
    <div>
      <Typography variant="h4">Suggestion de médicaments</Typography>

      {/* Medicaments Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Spécialité</TableCell>
            <TableCell>Forme</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Prix</TableCell>
            <TableCell>Indication</TableCell>
            <TableCell>Utilisateur</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {medicaments.map((medicament, idx) => (
            <TableRow 
              key={idx}
              hover
              onClick={() => handleRowClick(medicament)}
              style={{ cursor: 'pointer' }}
            >
              <TableCell>{medicament.specialite}</TableCell>
              <TableCell>{medicament.forme}</TableCell>
              <TableCell>{medicament.medoc_ou_complement_ou_phyto}</TableCell>
              <TableCell>{medicament.prix}</TableCell>
              <TableCell>{medicament.indication}</TableCell>
              <TableCell>{medicament?.userId?.username}</TableCell>
              <TableCell>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(e, medicament._id);
                  }}
                  size="small"
                  aria-label="actions"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="4" cy="12" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="20" cy="12" r="1.5" />
                  </svg>
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Détails du Médicament
        </DialogTitle>
        
        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedMedicament && (
            <Box display="flex" flexDirection="column" gap={3}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Informations Générales</Typography>
                <List dense>
                  <ListItem><ListItemText primary={renderField('DCI', selectedMedicament.dci)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Spécialité', selectedMedicament.specialite)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Forme', selectedMedicament.forme)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Dosage', selectedMedicament.dosage)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Quantité par boîte', selectedMedicament.qt_boite)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Prix', selectedMedicament.prix)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Code barre', selectedMedicament.barcode)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Code ART', selectedMedicament.art_code)} /></ListItem>
                  <ListItem><ListItemText primary={renderField('Type', selectedMedicament.medoc_ou_complement_ou_phyto)} /></ListItem>
                </List>
              </Paper>

              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Pathologies</Typography>
                {Array.isArray(selectedMedicament.pathologies) && selectedMedicament.pathologies.length > 0 ? (
                  <List dense>
                    {selectedMedicament.pathologies.map((item, index) => (
                      <ListItem key={index}><ListItemText primary={item} /></ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">Non spécifié</Typography>
                )}
              </Paper>

              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Appareils</Typography>
                {Array.isArray(selectedMedicament.appareils) && selectedMedicament.appareils.length > 0 ? (
                  <List dense>
                    {selectedMedicament.appareils.map((item, index) => (
                      <ListItem key={index}><ListItemText primary={item} /></ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">Non spécifié</Typography>
                )}
              </Paper>

              <Paper elevation={3} sx={{ p: 2 }}>
                {renderCompatibilities(selectedMedicament.compatibilites)}
              </Paper>

              <Paper elevation={3} sx={{ p: 2 }}>
                {renderPosologies(selectedMedicament.posologies)}
              </Paper>

              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Informations supplémentaires</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Mise en garde"
                      secondary={selectedMedicament.mise_en_garde || 'Non spécifié'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Durée de traitement"
                      secondary={selectedMedicament.duree_traitement || 'Non spécifié'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Conseil d'utilisation"
                      secondary={selectedMedicament.conseil_utilisation || 'Non spécifié'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Remarque"
                      secondary={selectedMedicament.remarque || 'Non spécifié'}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="outlined">Fermer</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}

export default MedicamentSuggestions;